// Document merge engine — renders a rich template's {{tokens}} against a client's
// data (core fields + any profile field, including custom ones the agency added).
// The same engine powers the Doc Hub's template editor preview and server-side
// generation, so the editor and the output can never drift.
/** Core tokens every client has, regardless of the agency's custom profile schema. */
export const DOC_CORE_TOKENS = [
    { id: 'full_name', label: 'Full name' },
    { id: 'first_name', label: 'First name' },
    { id: 'last_name', label: 'Last name' },
    { id: 'email', label: 'Email' },
    { id: 'phone', label: 'Phone' },
    { id: 'today', label: "Today's date" },
    { id: 'agency_name', label: 'Agency name' },
];
/** HTML-escape text (quotes included) before it lands in markup: merge values, email bodies, the
 *  print window's title. The ONE escaper — server mail and client print reuse it. */
export const escapeHtml = (s) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
/**
 * Replace {{token}} with the matching value (HTML-escaped). Unknown/empty tokens
 * render as an empty string. `values` is keyed by token id (core ids + profile field ids).
 */
export function renderDocTemplate(html, values) {
    return (html || '').replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_m, id) => {
        const v = values[id];
        return v == null || v === '' ? '' : escapeHtml(String(v));
    });
}
/** List the distinct merge tokens used in a template body. */
export function tokensInTemplate(html) {
    const out = new Set();
    for (const m of (html || '').matchAll(/\{\{\s*([\w.-]+)\s*\}\}/g))
        out.add(m[1]);
    return [...out];
}
/** Tags the rich editor produces — the only ones allowed through (no attributes ever). */
const ALLOWED_DOC_TAGS = new Set([
    'p', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'blockquote', 'hr', 'div', 'span',
]);
/** Elements dropped WITH their content (the rest of a non-allowlisted tag's text is kept). */
const BLOCK_TAGS = /<(script|style|iframe|object|embed|svg|math|template|noscript|link|meta|base|form)\b/gi;
/** Drop `<!-- … -->` comments in one linear pass (an unterminated one is left for the tag pass to escape). */
function stripComments(html) {
    let out = '';
    let i = 0;
    for (;;) {
        const open = html.indexOf('<!--', i);
        const close = open === -1 ? -1 : html.indexOf('-->', open + 4);
        if (close === -1)
            return out + html.slice(i);
        out += html.slice(i, open);
        i = close + 3;
    }
}
/**
 * Drop each dangerous element from its open tag through its close tag, in LINEAR time. The old lazy
 * regex rescanned to the end of the body for every unterminated `<script`, so a 2 MB body of them
 * pinned the event loop for over a minute. An unclosed block is left to the tag pass (tag dropped,
 * text kept, exactly as before). The per-name close search only ever moves forward.
 */
function stripBlocks(html) {
    const lower = html.toLowerCase();
    const nextClose = new Map();
    let out = '';
    let i = 0;
    for (const m of html.matchAll(BLOCK_TAGS)) {
        const at = m.index ?? 0;
        if (at < i)
            continue; // inside a block already dropped
        const name = m[1].toLowerCase();
        let close = nextClose.get(name);
        if (close === undefined || (close !== -1 && close <= at)) {
            close = lower.indexOf(`</${name}`, at + 1);
            nextClose.set(name, close);
        }
        if (close === -1)
            continue;
        const end = lower.indexOf('>', close);
        if (end === -1)
            continue;
        out += html.slice(i, at);
        i = end + 1;
    }
    return out + html.slice(i);
}
/** Longest body the sanitizer ever returns (and the most it will scan). */
const DOC_HTML_CAP = 200_000;
/** A `<` that would OPEN markup in the output — a tag, end tag, comment or declaration (the only cases
 *  the HTML tokenizer treats `<` as anything but text) — unless it is one of the normalized, attribute-
 *  free allowlisted tags the tag pass itself emits. Everything else (`<30`, `a < b`) is harmless text. */
const MARKUP_OPENER = new RegExp(`<(?!\\/?(?:${[...ALLOWED_DOC_TAGS].join('|')})(?: \\/)?>)(?=[a-zA-Z/!?])`, 'g');
/**
 * Strict allowlist sanitizer for document bodies (rich-text templates / generated docs).
 * These are authored by a team member but rendered to talent and the public, so a stored
 * <script>/onerror could exfiltrate a viewer's session — we drop dangerous element blocks,
 * remove every tag not on the formatting allowlist, and strip ALL attributes (no href, src,
 * style, or on*). Applied at the single write chokepoint AND again at render (defense in depth).
 * Linear time and idempotent: sanitizing sanitized output changes nothing.
 */
export function sanitizeDocHtml(html) {
    // Cap FIRST so no pass below ever sees more than the stored limit.
    const out = stripBlocks(stripComments((html || '').slice(0, DOC_HTML_CAP)))
        // Normalize every allowlisted tag (attributes stripped) and drop every other one. A tag's body stops
        // at the next `<`, so an unterminated `<a<a<a…` can't make each attempt rescan to the end.
        .replace(/<(\/?)([a-zA-Z][a-zA-Z0-9]*)\b[^<>]*?(\/?)>/g, (_m, slash, tag, selfClose) => {
        const t = tag.toLowerCase();
        if (!ALLOWED_DOC_TAGS.has(t))
            return '';
        return `<${slash}${t}${!slash && selfClose ? ' /' : ''}>`;
    })
        // Then, on the OUTPUT, escape any `<` that would still open markup. Judging the output is the point:
        // dropping `<x>` from `<<x>img onerror=…>` splices the first `<` onto `img`, and only this pass
        // sees that. Plain text keeps its `<30` and `>` exactly as typed (notes, site copy render as text).
        .replace(MARKUP_OPENER, '&lt;');
    // Escaping can lengthen the body; trim back to the cap without leaving half a tag at the end.
    return out.length > DOC_HTML_CAP ? out.slice(0, DOC_HTML_CAP).replace(/<[^>]*$/, '') : out;
}
/** True only for http(s) links — blocks javascript:/data:/vbscript: URLs stored as documents. */
export function isSafeHttpUrl(url) {
    return /^https?:\/\/[^\s]+$/i.test(url.trim());
}
//# sourceMappingURL=doctemplate.js.map